import { Component, HostListener, Input, OnInit, Type } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { FeatureFlagService } from 'src/app/services/feature-flags/feature-flag.service';
import { HoursCalculatorComponent } from '../hours-calculator/hours-calculator.component';
import { DatetimeSelectorV2Component } from '../datetime-selector-v2/datetime-selector-v2.component';

/// Second-generation hours keypad. Same modal contract as HoursCalculatorComponent -
/// inputs `valueNum`/`valueInfo`, dismisses with the number or null - so a caller
/// can swap one for the other without touching its onDidDismiss handler.
///
/// The only change is the ".5" toggle becoming a "." key, so any value with up to
/// two decimals can be entered (7.25, 6.87):
///   - whole hours keep v1's rule: built from the LAST TWO digits typed;
///   - "." starts the decimals (a second "." is ignored), and at most two decimal
///     digits are taken - further digits are ignored;
///   - the first key replaces the value the keypad opened with, as in v1, and OK
///     without typing returns that value untouched.
/// Meals and subsistence are counts, so "." is disabled for them.
///
/// Gated on TIME_PICKER_V2, the same flag as the v2 time picker - the two ship
/// together. Use `HoursCalculatorV2Component.pick()` when
/// opening the modal rather than checking the flag at each call site.
@Component({
  selector: 'app-hours-calculator-v2',
  templateUrl: './hours-calculator-v2.component.html',
  // Same look as v1 on purpose - share its stylesheet rather than fork it.
  styleUrls: ['../hours-calculator/hours-calculator.component.scss'],
})
export class HoursCalculatorV2Component implements OnInit {

  public static readonly FLAG = DatetimeSelectorV2Component.FLAG;

  /// The keypad component to open: v2 with the flag on, the original otherwise.
  public static pick(featureFlags: FeatureFlagService): Type<any> {
    return featureFlags.isEnabled(HoursCalculatorV2Component.FLAG)
      ? HoursCalculatorV2Component
      : HoursCalculatorComponent;
  }

  private static readonly COUNT_FIELDS = ['meals', 'subsistence'];

  @Input() valueNum: number;
  @Input() valueInfo: string;

  readonly selectorOptionsNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  allowsDecimals = true;

  // `touched` stays false until the first key, so the opening value is shown
  // (and returned) exactly as it came in.
  private touched = false;
  private whole = '';
  private fraction: string | null = null;   // null until "." is pressed

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    this.allowsDecimals = !HoursCalculatorV2Component.COUNT_FIELDS.includes(this.valueInfo);
  }

  get showsHrs(): boolean {
    return this.allowsDecimals;
  }

  get isTyping(): boolean {
    return this.touched;
  }

  /// What the header shows while typing - kept as typed, so "7." keeps its dot.
  get entry(): string {
    return (this.whole || '0') + (this.fraction !== null ? '.' + this.fraction : '');
  }

  get initialValue(): number {
    return this.valueNum ?? 0;
  }

  pressDigit(digit: number) {
    this.touched = true;
    if (this.fraction === null) {
      // Number() drops the leading zero of "07" so the display never shows it.
      this.whole = String(Number((this.whole + digit).slice(-2)));
    } else if (this.fraction.length < 2) {
      this.fraction += digit;
    }
  }

  pressDot() {
    if (!this.allowsDecimals) {
      return;
    }
    this.touched = true;
    if (this.fraction === null) {
      this.fraction = '';
    }
  }

  clearHours() {
    this.touched = true;
    this.whole = '';
    this.fraction = null;
  }

  updateHours() {
    this.modalController.dismiss(this.touched ? Number(this.entry) : this.initialValue);
  }

  closeModal() {
    this.modalController.dismiss(null);
  }

  // A hardware keyboard (desktop, tablet with keyboard) drives the same keys.
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (/^[0-9]$/.test(event.key)) {
      this.pressDigit(Number(event.key));
    } else if (event.key === '.' || event.key === ',') {
      this.pressDot();
    } else if (event.key === 'Enter') {
      this.updateHours();
    } else if (event.key === 'Escape') {
      this.closeModal();
    } else {
      return;
    }
    event.preventDefault();
  }
}
